import React, { PropsWithChildren } from 'react';

import { formatMoney } from '@betterment/core';
import { Box, HStack, ShimmerText, Text, VStack } from '@betterment/samba-react';
import { formatNumberWithDecimals } from '@betterment/shared-components';

import { EnvelopeRowMenu } from './EnvelopeRowMenu';
import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';
import { HouseholdEnvelopeYtdReturnsQuery } from './graphql/query.householdEnvelopeYtdReturns.generated';
import { RowCell } from './RowCell';
import { TargetField } from './TargetField';
import { formatAccountHolders } from './utils';

type Envelope = HouseholdAccountsQuery['household']['accounts'][number]['envelope'];
type AccountHolder = HouseholdAccountsQuery['household']['accounts'][number]['accountHolders'][number];
type YtdReturn = HouseholdEnvelopeYtdReturnsQuery['household']['envelopeYtdReturns'][number]['ytdReturn'];

function EnvelopeRowLayout({ children, showYtdReturns }: PropsWithChildren<{ showYtdReturns: boolean }>) {
  const gridColumns = showYtdReturns
    ? 'minmax(auto, 140px) 1fr 1fr minmax(auto, 150px) minmax(auto, 200px) 32px'
    : 'minmax(auto, 140px) 1fr 1fr minmax(auto, 200px) 32px';

  return (
    <Box
      css={{
        width: '100%',
        gridColumn: '1 / -1',
      }}
      role="row"
    >
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gridAutoRows: 'auto',
          color: 'text',
          paddingTop: 'space.300',
          paddingBottom: 'space.300',
          borderTop: 'solid',
          borderWidth: 'thin',
          borderColor: 'border.weak',
          minHeight: '112px',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export const EnvelopeRow: React.FC<{
  envelope: Envelope;
  accountHolders: AccountHolder[];
  ytdReturn?: YtdReturn;
  ytdReturnsLoading: boolean;
  showYtdReturns: boolean;
}> = ({ envelope, accountHolders, ytdReturn, ytdReturnsLoading, showYtdReturns }) => {
  if (!envelope.purpose) {
    return null;
  }

  const iconUrl = envelope.purpose.icon.light;
  const formattedAccountHolders = formatAccountHolders(accountHolders);

  return (
    <EnvelopeRowLayout key={envelope.id} showYtdReturns={showYtdReturns}>
      <RowCell css={{ gridColumn: '1' }}>
        <Box>
          <Text>{formattedAccountHolders}</Text>
        </Box>
      </RowCell>
      <RowCell css={{ gridColumn: '2' }}>
        <HStack css={{ alignItems: 'center', gap: 'space.150' }}>
          <Box css={{ width: 'size.450', height: 'size.450' }}>
            <img src={iconUrl} alt="Icon" />
          </Box>
          <VStack>
            <Text color="text" className="goal-title">
              {envelope.purpose.name}
            </Text>
          </VStack>
        </HStack>
      </RowCell>
      <RowCell css={{ gridColumn: '3' }}>
        <TargetField purpose={envelope.purpose} />
      </RowCell>
      {showYtdReturns && (
        <RowCell css={{ gridColumn: '4', justifyContent: 'end', paddingRight: 'space.300' }}>
          {ytdReturnsLoading && <ShimmerText textStyle="text100" css={{ maxWidth: '80%' }} />}
          {!ytdReturnsLoading && ytdReturn && (
            <VStack css={{ alignItems: 'flex-end', gap: 'space.50' }}>
              {ytdReturn.amount !== null && ytdReturn.amount !== undefined && (
                <Text variant="text100" color={ytdReturn.amount.cents > 0 ? 'text.success' : 'text'}>
                  {ytdReturn.amount.cents > 0 ? '+' : ''}
                  {formatMoney(ytdReturn.amount)}
                </Text>
              )}
              <Text
                variant="text50"
                color={
                  ytdReturn.moneyWeighted !== null &&
                  ytdReturn.moneyWeighted !== undefined &&
                  ytdReturn.moneyWeighted > 0
                    ? 'text.success'
                    : 'text'
                }
              >
                {ytdReturn.moneyWeighted !== null && ytdReturn.moneyWeighted !== undefined
                  ? `${ytdReturn.moneyWeighted > 0 ? '+' : ''}${formatNumberWithDecimals(ytdReturn.moneyWeighted, 2)}%`
                  : '-'}
              </Text>
            </VStack>
          )}
          {!ytdReturnsLoading && !ytdReturn && <Text color="text.weak">-</Text>}
        </RowCell>
      )}
      <RowCell css={{ gridColumn: showYtdReturns ? '5' : '4', justifyContent: 'end', paddingRight: 'space.300' }}>
        <Text>{envelope.balance ? formatMoney(envelope.balance) : ''}</Text>
      </RowCell>
      <RowCell>
        <EnvelopeRowMenu envelope={envelope} />
      </RowCell>
    </EnvelopeRowLayout>
  );
};
